/**
 * §2 `IdAllocator` + contracts/adapter.md §4 — THE SOLE ID AUTHORITY.
 *
 * A session is THE PAGE, not the worker instance, and workers get terminated (adapter §3.2).
 * So the seed lives on the main thread:
 *
 *   epoch     random token, minted once per PAGE, passed in `init`
 *   counter   integer, advanced by the worker, returned on EVERY `result`
 *
 * The main thread keeps the maximum counter it has seen and passes it back in `init` on
 * respawn, so a restarted worker can never re-issue an id a live document already holds.
 *
 * `cueFor(key)` is derived from the KEY, not the counter, so it survives a restart and is
 * stable for the page lifetime — that is what makes nudging maxCells from 42 to 41 diff as
 * EDITS instead of 100% churn.
 */

import type {
  CueId,
  DocumentId,
  IdAllocator,
  InputId,
  RegionId,
  RunId,
  StyleId,
  TrackId,
} from '../../../contracts/cue-model.ts';

/**
 * 64-bit FNV-1a as two 32-bit halves, rendered base36.
 *
 * Why not one 32-bit hash: `cueFor` keys a whole document's cue identities. At 100,000 cues
 * a 32-bit space has a ~1-in-1,000 chance of at least one collision by the birthday bound,
 * and a cue-id collision aliases two cues in the diff, the undo journal and the metrics
 * cache at once. 64 bits puts it past 1-in-10^9 and costs one extra pass.
 *
 * Deterministic and dependency-free on purpose: golden runs depend on it, and it has to port
 * to Python mechanically.
 */
export function stableHash(key: string): string {
  let h1 = 0x811c9dc5; // FNV offset basis
  let h2 = 0x01000193; // a second basis, so the halves are independent
  for (let i = 0; i < key.length; i++) {
    const c = key.charCodeAt(i);
    h1 ^= c;
    h1 = Math.imul(h1, 0x01000193) >>> 0;
    h2 ^= c + i;
    h2 = Math.imul(h2, 0x85ebca6b) >>> 0;
  }
  return h1.toString(36).padStart(7, '0') + h2.toString(36).padStart(7, '0');
}

export interface AllocatorSeed {
  /** Random token minted once per PAGE. Tests inject a fixed value. */
  readonly epoch: string;
  /** Counter to resume from. Tests inject 0. */
  readonly counter: number;
}

export interface CoreIdAllocator extends IdAllocator {
  readonly epoch: string;
  /** The current counter, to be returned to the main thread on EVERY `result`. */
  counter(): number;
}

/** Prefixes are single letters; `cueFor` uses `c-` so a derived id can never collide with a
 *  counter id (which always has a digit immediately after the prefix). */
const PREFIX = {
  cue: 'c',
  run: 'r',
  style: 's',
  region: 'g',
  track: 't',
  document: 'd',
  input: 'i',
} as const;

export function createIdAllocator(seed: AllocatorSeed): CoreIdAllocator {
  if (typeof seed.epoch !== 'string' || seed.epoch === '') {
    throw new RangeError('IdAllocator: epoch must be a non-empty string minted once per page');
  }
  if (!Number.isInteger(seed.counter) || seed.counter < 0) {
    throw new RangeError(
      `IdAllocator: counter must be a non-negative integer, got ${seed.counter}`,
    );
  }
  const epoch = seed.epoch;
  let n = seed.counter;
  const mint = (prefix: string): string => `${epoch}-${prefix}${n++}`;

  return {
    epoch,
    counter: () => n,
    cue: () => mint(PREFIX.cue) as CueId,
    cueFor: (key: string) => `${epoch}-${PREFIX.cue}-${stableHash(key)}` as CueId,
    run: () => mint(PREFIX.run) as RunId,
    style: () => mint(PREFIX.style) as StyleId,
    region: () => mint(PREFIX.region) as RegionId,
    track: () => mint(PREFIX.track) as TrackId,
    document: () => mint(PREFIX.document) as DocumentId,
    input: () => mint(PREFIX.input) as InputId,
  };
}

/**
 * Mints a page epoch. Uses `crypto.randomUUID` when the runtime has it and falls back to a
 * timestamp-plus-random token otherwise — the epoch only has to be unique across the pages a
 * single user has open, not globally, because it is never persisted.
 *
 * Called on the MAIN THREAD, once per page. The worker never mints one; it is handed one in
 * `init` and would otherwise re-issue ids after a respawn.
 */
export function mintEpoch(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID().slice(0, 8);
  return (
    Math.floor(Date.now() / 1000).toString(36) + Math.floor(Math.random() * 0x1000000).toString(36)
  );
}
